/**
 * DocumentCard — Dropbox-style grid tile: a large preview area (centred
 * file-type icon on a soft surface) with hover actions, over a slim footer
 * showing the filename + meta. Clicking the preview downloads/opens the file.
 */
import { Download, Trash2, Sparkles } from "lucide-react";
import { cn } from "@flint/ui";
import { formatBytes, fileKind } from "../lib/format.ts";
import { docTypeSpec } from "./doc-avatar.tsx";
import type { KbStatus } from "./kb-status.tsx";

export interface DocumentCardItem {
  id: string;
  title: string;
  originalName: string;
  contentType: string;
  sizeBytes: string;
  tags: string[];
  isKb: boolean;
  kbStatus: KbStatus;
  uploadedAt: string;
}

export function DocumentCard({
  document: d,
  onDownload,
  onDelete,
  onToggleKb,
}: {
  document: DocumentCardItem;
  onDownload: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleKb: (id: string, enabled: boolean) => void;
}) {
  const spec = docTypeSpec(d.contentType);
  const Icon = spec.icon;

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-lg border border-line bg-paper transition-all hover:border-line-2 hover:shadow-md">
      {/* Preview region */}
      <button
        type="button"
        onClick={() => onDownload(d.id)}
        title={`Open ${d.title}`}
        className="relative flex h-28 w-full items-center justify-center bg-bg"
      >
        <Icon size={40} strokeWidth={1.5} className={cn(spec.fg)} aria-hidden />

        {/* KB badge (always visible when on) */}
        {d.isKb && (
          <span
            className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-sm bg-sky-50 px-1.5 py-0.5 text-xs font-semibold text-sky-700 ring-1 ring-sky-100"
            title={d.kbStatus === "indexed" ? "In knowledge base" : `Knowledge base: ${d.kbStatus}`}
          >
            <Sparkles size={10} aria-hidden /> KB
          </span>
        )}

        {/* Hover actions */}
        <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              onToggleKb(d.id, !d.isKb);
            }}
            title={d.isKb ? "Remove from knowledge base" : "Add to knowledge base"}
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-md border shadow-sm",
              d.isKb
                ? "border-sky-200 bg-sky-50 text-sky-700"
                : "border-line bg-paper text-muted hover:text-ink",
            )}
          >
            <Sparkles size={13} aria-hidden />
          </span>
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              onDownload(d.id);
            }}
            title="Download"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-paper text-muted shadow-sm hover:text-ink"
          >
            <Download size={13} aria-hidden />
          </span>
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(d.id);
            }}
            title="Delete"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-paper text-muted shadow-sm hover:border-bad-100 hover:bg-bad-50 hover:text-bad"
          >
            <Trash2 size={13} aria-hidden />
          </span>
        </div>
      </button>

      {/* Footer */}
      <div className="border-t border-line px-3 py-2">
        <p className="truncate text-xs font-semibold text-ink" title={d.title}>
          {d.title}
        </p>
        <p className="mt-0.5 truncate text-xs text-muted">
          {fileKind(d.contentType)} · {formatBytes(d.sizeBytes)}
        </p>
      </div>
    </div>
  );
}
